import {_} from 'lodash';
import * as ko from "knockout";
import {MAX_RESULTS_PER_PAGE, ObjectTypeId} from "../../constants";
import {
    getAnyValue,
    getNextId,
    getObjectDefinition,
    getObjectDisplayName,
    getObjectType,
    getRawSyncFieldValue
} from "../../util/misc";
import {TSyncfield_Department} from "../../constants/Syncfields";
import {searchObjectWithSyncfields} from "../../util/request";
import {global, lm} from "../../appContext";
import {IComboboxV2Options} from "./v2";
import {nsTooltip} from "../../util/nsTooltip";
import {nsSyncFields} from "../../req/nsSyncFields";
import {IKnockoutComponentDialogContent} from "../dialog/manager";
import {getHash, getRouteDefinition} from "../../constants/RouteDefinitions";
import {IObjectData, IRouteDefinitionParams, ISyncField} from "../../index";
import {ImportFormatId} from "../../constants/ImportFormats";
import departmentHtml from './department.html?raw';

export const template = departmentHtml;

export type TObjectData_basicDepartment = nsSyncFields.TObjectData_basic & { name: string }

export interface IComboboxOptions_Department {
    comboboxOptions?: Partial<IComboboxV2Options<TObjectData_basicDepartment>>
    keys?: TSyncfield_Department[]
    showIcon?: boolean
    showRoot?: boolean
}

export class viewModel {
    /** Merged options object shared with v2 (reload / isReady written onto this). */
    private readonly v2Options: IComboboxV2Options<TObjectData_basicDepartment>
    private readonly selected: KnockoutObservable<TObjectData_basicDepartment | null>
    private readonly objectTypeIdsSubscription?: KnockoutSubscription;
    private readonly isReady: KnockoutObservable<boolean> = ko.observable(false);
    private readonly keys: TSyncfield_Department[];
    private readonly showIcon: boolean
    private readonly syncfieldName: TSyncfield_Department = global.login.selectedOrganization()?.importformat?.id == ImportFormatId.Visma_HR8 ? 'DepartmentNumber' : 'OrgUnitnr'
    /** Unique CSS anchor so each ⋮ menu can use fixed positioning without colliding. */
    private readonly functionsAnchorName = `--combobox-dept-fn-${getNextId()}`
    private readonly functionsAnchorStyle = {'anchor-name': this.functionsAnchorName}
    private readonly functionsMenuStyle = {'position-anchor': this.functionsAnchorName}

    constructor(private params: IComboboxOptions_Department) {
        this.showIcon = params.showIcon === true
        const callerOptions = params.comboboxOptions ?? {}
        this.selected = callerOptions.selected ?? ko.observable<TObjectData_basicDepartment | null>(null)
        this.keys = params.keys ?? ['Id', 'ParentDepartmentNumber', ...(getObjectDefinition(ObjectTypeId.Department)?.syncfieldDisplayName as TSyncfield_Department[] ?? [])]

        const type = getObjectType(ObjectTypeId.Department)
        const defaults: IComboboxV2Options<TObjectData_basicDepartment> = {
            inputAttrs: {placeholder: lm.t('none selected')},
            isReady: this.isReady,
            option: {
                label: o => o?.name ?? '',
                tooltip: o => o ? lm.t(nsTooltip.getTitle_objectdata(o) ?? '') : '',
                sortBy: this.params.showRoot ? [o => o.id !== 0] : [],
            },
            selected: this.selected,
        };

        const useDefaultPromise = !callerOptions.promise && !callerOptions.data
        if (useDefaultPromise) {
            // Factory — reload must start a new fetch, not replay one settled Promise.
            defaults.promise = () => searchObjectWithSyncfields(
                ObjectTypeId.Department,
                this.keys,
                {resultsprpage: MAX_RESULTS_PER_PAGE, onlytuples: true},
            ) as Promise<any>
            // Tuple → object conversion only for the API promise path. Callers that pass
            // `data` already supply department objects (e.g. user wizard).
            defaults.prepareData = {
                reduce: (arr: any[]) => this.mapTuplesToDepartments(arr, type),
            }
        } else if (this.params.showRoot === true) {
            defaults.prepareData = {
                map: (items) => this.prependRootDepartment(items.slice(), type),
            }
        }

        this.v2Options = _.defaultsDeep(callerOptions, defaults) as IComboboxV2Options<TObjectData_basicDepartment>
        if (this.v2Options.data != null) {
            delete this.v2Options.promise
        }
    }

    /** Convert searchobject tuple rows into department objects with display names. */
    private mapTuplesToDepartments(arr: any[], type: ReturnType<typeof getObjectType>): TObjectData_basicDepartment[] {
        const ret: TObjectData_basicDepartment[] = arr.map((o: string[]) => {
            const obj = nsSyncFields.convertTuplesToObjectData(type, this.keys, o);
            const name = this.getDisplayName(obj)
            return _.defaults({name: name}, obj)
        })
        if (this.params.showRoot === true) {
            return this.prependRootDepartment(ret, type)
        }
        return ret
    }

    /** Insert the synthetic root department at the front of a prepared list. */
    private prependRootDepartment(
        items: TObjectData_basicDepartment[],
        type: ReturnType<typeof getObjectType>,
    ): TObjectData_basicDepartment[] {
        if (items.some(o => o.id === 0)) {
            return items
        }
        const obj: TObjectData_basicDepartment = {
            id: 0,
            name: '',
            syncfields: [{
                name: 'Department',
                value: lm.t('root'),
            }, {
                name: 'DepartmentNumber',
                value: '0',
            }, {
                name: 'OrgUnitnr',
                value: '0',
            }] as ISyncField[],
            type: type,
        }
        obj.name = this.getDisplayName(obj)
        items.unshift(obj)
        return items
    }

    private getDisplayName(o: nsSyncFields.TObjectData_basic): string {
        const name = getObjectDisplayName(o);
        const identifier = getRawSyncFieldValue(this.syncfieldName, o.syncfields);
        return `${name} (${identifier})`
    }

    private onHierarchyClick() {
        const params: IRouteDefinitionParams & any = {
            __routeParams: {id: this.selected()?.id},
            __routeDef: getRouteDefinition('departmenthierarchy', {
                labelLong: 'choose subject|s:((department|ac))',
            }),
            __dialog: {width: 'auto'},
            onSelect: (o: IObjectData, e: JQuery.ClickEvent) => {
                e.stopPropagation()
                const name = this.getDisplayName(o)
                const obj: TObjectData_basicDepartment = _.defaultsDeep({name: name}, o)
                this.selected(obj)
                component.params.__dialog.close()
            },
        }

        const component: IKnockoutComponentDialogContent = {
            name: 'component/department/hierarchy',
            params: params
        }
        global.dialogManager.addCustom(component)
    }

    private isHierarchyEnabled(): boolean {
        const a = this.isReady();
        const b = getAnyValue(this.v2Options.enable ?? true);
        return a && b
    }

    private onPreviewClick() {
        const id = this.selected()?.id
        if (id == null) {
            return
        }
        const hash = getHash('departmentdetails', {id})
        window.open(hash ?? '', '_blank');
    }

    private isPreviewEnabled(): boolean {
        return !!this.selected()?.id
    }

    dispose() {
        this.objectTypeIdsSubscription?.dispose()
    }
}
